/**
 * 学生端 UI 主题集中配置（阶段一：前端预设）
 * ============================================================
 * 本文件的数据结构即未来"后台 UI 管理"下发的数据形状：
 * 阶段二只需把 THEMES/ROUTES 挪到数据库 + 接口下发，applyTheme 引擎不变。
 *
 * 概念：
 * - 主题(theme) = 一组设计 token（色板/品牌/背景），所有界面颜色都引用 token
 * - 路由(route)  = 按 客户(corpId) / 学段(position: 1小学 2初中 3高中) 选择主题
 *   匹配优先级：corpId 精确匹配 > position 匹配 > defaultTheme
 *
 * token 语义键说明（applyTheme 引擎负责映射到 CSS 变量）：
 *   void        页面深空底色            text       正文
 *   panel       面板底                  textDim    次要文字
 *   panelDeep   弹层/下拉深底           textBright 高亮文字
 *   panelHead   面板头条底              success    成功/掌握
 *   primary     主色（数据/激活/动作）   danger     告警
 *   primaryDeep 主色深（实色渐变端）     nebula1~3  背景星云光色(含透明度)
 *   primaryBright 主色亮（实色渐变端）   starsOpacity 星野整体亮度 0~1
 *   accent      辅色（计数/目录/警示柔和强调）
 */
export const THEME_CONFIG = {
    version: 1,
    defaultTheme: 'hud-cyan',

    /* 主题路由：先到先得。需要按客户换皮时，取消注释并填真实 corp_id */
    routes: [
        // { corpId: 10001, themeId: 'customer-violet' },   // 示例：某客户定制紫罗兰
        { position: 1, themeId: 'primary-warm' },           // 小学学段 → 暖色活力版
        // { position: 2, themeId: 'hud-cyan' },            // 初中 → 默认深空（可不配）
    ],

    themes: {
        /* ---------- 默认：深空 HUD · 青（初高中） ---------- */
        'hud-cyan': {
            themeId: 'hud-cyan',
            name: '深空HUD · 青',
            themeClass: 'theme-hud-cyan',
            brand: {
                productName: '知明慧学 · 学生端',
                subName: 'STUDENT CONSOLE',
                logoUrl: ''     // 留空则用内置 CSS 三角标记；填 OSS 地址则显示图片
            },
            tokens: {
                void: '#030711',
                panel: 'rgba(8, 16, 30, 0.82)',
                panelDeep: 'rgba(5, 11, 22, 0.92)',
                panelHead: 'rgba(14, 28, 48, 0.65)',
                primary: '#4fd8ff',
                primaryRgb: '79, 216, 255',
                primaryDeep: '#2fa8d8',
                primaryDeepRgb: '47, 168, 216',
                primaryBright: '#57c7f0',
                onPrimary: '#ffffff',
                accent: '#ffb545',
                accentRgb: '255, 181, 69',
                success: '#46f0a8',
                danger: '#ff5f6b',
                text: '#c9dcee',
                textDim: '#6d84a3',
                textBright: '#eaf6ff',
                nebula1: 'rgba(31, 84, 122, 0.30)',
                nebula2: 'rgba(24, 60, 110, 0.24)',
                nebula3: 'rgba(16, 42, 84, 0.30)',
                starsOpacity: '0.55'
            }
        },

        /* ---------- 小学：暖色活力版（明黄主色 + 珊瑚辅色 + 更亮底） ---------- */
        'primary-warm': {
            themeId: 'primary-warm',
            name: '暖色活力 · 小学',
            themeClass: 'theme-primary-warm',
            brand: {
                productName: '知明慧学 · 学生端',
                subName: 'SUNNY CONSOLE',
                logoUrl: ''
            },
            tokens: {
                void: '#0a0e20',
                panel: 'rgba(19, 26, 52, 0.85)',
                panelDeep: 'rgba(14, 19, 40, 0.94)',
                panelHead: 'rgba(30, 40, 72, 0.70)',
                primary: '#ffd45e',
                primaryRgb: '255, 212, 94',
                primaryDeep: '#f5a623',
                primaryDeepRgb: '245, 166, 35',
                primaryBright: '#ffe08a',
                onPrimary: '#241500',
                accent: '#ff8a5e',
                accentRgb: '255, 138, 94',
                success: '#6df0a8',
                danger: '#ff6b7d',
                text: '#f2ecda',
                textDim: '#a39c88',
                textBright: '#fff8e6',
                nebula1: 'rgba(120, 84, 31, 0.28)',
                nebula2: 'rgba(110, 60, 24, 0.22)',
                nebula3: 'rgba(84, 42, 16, 0.28)',
                starsOpacity: '0.7'
            }
        },

        /* ---------- 示例客户定制：紫罗兰（演示按客户换皮） ---------- */
        'customer-violet': {
            themeId: 'customer-violet',
            name: '紫罗兰 · 客户定制',
            themeClass: 'theme-customer-violet',
            brand: {
                productName: '知明慧学 · 学生端',
                subName: 'VIOLET CONSOLE',
                logoUrl: ''
            },
            tokens: {
                void: '#070512',
                panel: 'rgba(16, 12, 32, 0.82)',
                panelDeep: 'rgba(11, 8, 24, 0.92)',
                panelHead: 'rgba(26, 20, 48, 0.65)',
                primary: '#b28bff',
                primaryRgb: '178, 139, 255',
                primaryDeep: '#8b5cf6',
                primaryDeepRgb: '139, 92, 246',
                primaryBright: '#c9a8ff',
                onPrimary: '#ffffff',
                accent: '#ffd45e',
                accentRgb: '255, 212, 94',
                success: '#46f0a8',
                danger: '#ff5f6b',
                text: '#ded4f5',
                textDim: '#8d80ab',
                textBright: '#f3edff',
                nebula1: 'rgba(76, 42, 122, 0.30)',
                nebula2: 'rgba(58, 32, 96, 0.24)',
                nebula3: 'rgba(40, 22, 70, 0.30)',
                starsOpacity: '0.5'
            }
        }
    }
};

/** 所有主题的 body class（切换时用于整旧挂新） */
export const ALL_THEME_CLASSES = Object.values(THEME_CONFIG.themes).map(t => t.themeClass).concat(['theme-custom']);
